import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Icon, { type IconName } from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import { BROCHURE_MODULES, type ModuleSlug } from "../../data/brochureModules";
import { useMySubscription } from "../../hooks/useMySubscription";
import { track, AnalyticsEvents } from "../../utils/analytics";

/**
 * Shared template for every per-module brochure (Phase 6).
 *
 * Design rationale (impeccable: hierarchy + restraint):
 *   - These pages are the sales pitch. The job is to convince an HR lead or
 *     admin that the trial is worth flipping on — not to overwhelm them with
 *     marketing chrome. So: one calm hero, one strip of evidence, one grid of
 *     specifics, one synthetic screenshot, one persona band, one closing CTA.
 *     Six beats, in order. No surprises.
 *
 *   - We deliberately reuse the BrochureOverlay's surface tones (white → slate-50
 *     gradient, slate-200 hairlines, indigo-600 primary) so the journey from
 *     overlay → brochure feels like the same product, not two different teams.
 *
 *   - Mockups are SVG/Tailwind compositions. Stock images cheapen the page; a
 *     faux-app screenshot rendered with the same tokens the real app uses says
 *     "we built this thing, look how it actually feels".
 *
 *   - RTL: every flex/grid uses logical properties (start/end). The hero swaps
 *     order via flex-row-reverse when RTL so the illustration always sits on
 *     the leading edge of the reading direction.
 *
 *   - Tier-aware CTAs come from useMySubscription. We don't double-render the
 *     sticky banner; we just expose the right primary action for the tier.
 */

// ---------- Public types — peer subagents extend these for their brochures ----------

export interface BrochureHeroContent {
  /** Small uppercase label above the headline, e.g. "Time & Attendance". */
  eyebrow: string;
  /** Main h1, ~6-9 words. Sentence-cased, ends in a period if it's a statement. */
  headline: string;
  /** 1-2 sentences expanding the promise. Muted slate-600. */
  subheadline: string;
  /** Optional right-side visual. Build from SVG/Tailwind — no stock images. */
  illustration?: React.ReactNode;
}

export interface BrochureFeature {
  /** lucide-react icon name. Must exist on the IconName union. */
  icon: IconName;
  title: string;
  /** 1-2 sentences. Be specific — name a behavior, not a category. */
  description: string;
}

export interface BrochureMetric {
  /** e.g. "67%". Renders with tabular-nums so columns align across cards. */
  value: string;
  /** Sub-label, e.g. "less time on weekly timesheets". Read it aloud. */
  label: string;
}

export interface BrochureMockup {
  /** Small caption shown under the mockup frame. */
  title: string;
  /**
   * Faux-app screenshot built from React + Tailwind primitives — calendar
   * grids, table rows, status pills, mini-charts. Use the real app's tokens
   * (slate borders, indigo/amber/emerald accents) so it reads as a screenshot.
   */
  content: React.ReactNode;
}

export interface BrochureUseCase {
  /** Persona label, e.g. "HR Manager". Eyebrow-styled. */
  persona: string;
  /** 1 sentence — the pain. */
  scenario: string;
  /** 1 sentence — the outcome. Renders after a subtle "→" separator. */
  outcome: string;
}

export interface BrochureTestimonial {
  quote: string;
  author: string;
  role: string;
}

export interface ModuleBrochureProps {
  slug: ModuleSlug;
  hero: BrochureHeroContent;
  /** 4-6 feature cards. The grid auto-tunes to 2 cols at >=6, 3 cols at <=5. */
  features: BrochureFeature[];
  /** 3 stat cards. Omit to skip the metric strip entirely. */
  metrics?: BrochureMetric[];
  /** 1 large in-page mockup. Omit to skip the mockup section entirely. */
  mockup?: BrochureMockup;
  /** 2-3 persona cards. Omit to skip the use-cases band entirely. */
  useCases?: BrochureUseCase[];
  /** Optional, off by default. Keep it quiet if you use it. */
  testimonial?: BrochureTestimonial;
}

// ---------- Helpers — accent → gradient + ring resolution ----------

/**
 * The module accent in `brochureModules.ts` is built for icon chips, so it
 * only carries surface + iconText + ring. The brochure needs a couple more
 * variants — a soft hero-background gradient and a primary-button override —
 * so we extend the palette here keyed on the same slug.
 *
 * If a slug is missing we fall back to indigo, matching the BrochureOverlay.
 */
const ACCENT_BY_SLUG: Record<
  ModuleSlug,
  {
    heroGradientFrom: string; // tailwind class — top-left wash of the hero
    heroGradientTo: string; // tailwind class — bottom-right wash of the hero
    primaryBg: string;
    primaryBgHover: string;
    primaryRing: string;
    chipText: string;
    chipBg: string;
    metricText: string;
  }
> = {
  attendance: {
    heroGradientFrom: "from-indigo-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-indigo-600",
    primaryBgHover: "hover:bg-indigo-700",
    primaryRing: "focus-visible:ring-indigo-500",
    chipText: "text-indigo-700",
    chipBg: "bg-indigo-50",
    metricText: "text-indigo-700",
  },
  leaves: {
    heroGradientFrom: "from-amber-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-amber-600",
    primaryBgHover: "hover:bg-amber-700",
    primaryRing: "focus-visible:ring-amber-500",
    chipText: "text-amber-800",
    chipBg: "bg-amber-50",
    metricText: "text-amber-800",
  },
  recruitment: {
    heroGradientFrom: "from-sky-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-sky-600",
    primaryBgHover: "hover:bg-sky-700",
    primaryRing: "focus-visible:ring-sky-500",
    chipText: "text-sky-700",
    chipBg: "bg-sky-50",
    metricText: "text-sky-700",
  },
  impact: {
    heroGradientFrom: "from-rose-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-rose-600",
    primaryBgHover: "hover:bg-rose-700",
    primaryRing: "focus-visible:ring-rose-500",
    chipText: "text-rose-700",
    chipBg: "bg-rose-50",
    metricText: "text-rose-700",
  },
  development: {
    heroGradientFrom: "from-emerald-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-emerald-600",
    primaryBgHover: "hover:bg-emerald-700",
    primaryRing: "focus-visible:ring-emerald-500",
    chipText: "text-emerald-800",
    chipBg: "bg-emerald-50",
    metricText: "text-emerald-800",
  },
  payroll: {
    heroGradientFrom: "from-violet-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-violet-600",
    primaryBgHover: "hover:bg-violet-700",
    primaryRing: "focus-visible:ring-violet-500",
    chipText: "text-violet-800",
    chipBg: "bg-violet-50",
    metricText: "text-violet-800",
  },
  tournament: {
    heroGradientFrom: "from-orange-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-orange-600",
    primaryBgHover: "hover:bg-orange-700",
    primaryRing: "focus-visible:ring-orange-500",
    chipText: "text-orange-700",
    chipBg: "bg-orange-50",
    metricText: "text-orange-700",
  },
  projects: {
    heroGradientFrom: "from-teal-50",
    heroGradientTo: "to-white",
    primaryBg: "bg-teal-600",
    primaryBgHover: "hover:bg-teal-700",
    primaryRing: "focus-visible:ring-teal-500",
    chipText: "text-teal-800",
    chipBg: "bg-teal-50",
    metricText: "text-teal-800",
  },
};

// ---------- Component ----------

const ModuleBrochure: React.FC<ModuleBrochureProps> = ({
  slug,
  hero,
  features,
  metrics,
  mockup,
  useCases,
  testimonial,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";

  const moduleEntry = BROCHURE_MODULES.find((m) => m.slug === slug);
  const accent = ACCENT_BY_SLUG[slug];
  const moduleName = moduleEntry ? t(moduleEntry.nameKey) : slug;

  const { isFree, isTrial, isPaid } = useMySubscription();

  useEffect(() => {
    track(AnalyticsEvents.BrochureViewed, { module: slug });
  }, [slug]);

  // Feature grid tunes its column count to keep widows tidy:
  //   - 4 features → 2 cols on desktop
  //   - 5 features → 3 cols on desktop (one trailing card pulls right)
  //   - 6+ features → 3 cols on desktop, 2 on tablet
  const featureGridCols =
    features.length <= 4 ? "lg:grid-cols-2" : "lg:grid-cols-3";

  // Primary CTA in the breadcrumb + closing band depends on tier.
  const primaryCta = (() => {
    if (isPaid) return { label: t("brochure.cta.allSet"), kind: "pill" as const };
    if (isTrial) return { label: t("brochure.cta.trialStarted"), kind: "pill" as const };
    if (isFree) return { label: t("Start free trial"), kind: "button" as const };
    // Operator or unknown — keep the door open with a friendly CTA.
    return { label: t("Start free trial"), kind: "button" as const };
  })();

  const handleStartTrial = () => {
    track(AnalyticsEvents.StartTrialCtaClicked, { source: "brochure", module: slug });
    navigate("/start-trial");
  };
  const handleBackToWorkspace = () => navigate("/dashboard");
  // Pricing lives on the marketing site (mowazaf.com/pricing) — open in a new
  // tab so the in-app brochure context isn't lost. Anonymous visitors hitting
  // /brochure/* directly couldn't reach an in-app pricing surface anyway.
  const MARKETING_PRICING_URL = "https://mowazaf.com/pricing/";
  const MARKETING_CONTACT_URL = "https://mowazaf.com/contact-us/";
  const handleSeePricing = () =>
    window.open(MARKETING_PRICING_URL, "_blank", "noopener,noreferrer");
  const handleContactUs = () =>
    window.open(MARKETING_CONTACT_URL, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-[calc(100vh-108px)] bg-gradient-to-b from-white via-slate-50 to-white text-slate-900">
      {/* ===== Sticky breadcrumb ===== */}
      <header
        className={cn(
          "sticky top-0 z-30 border-b border-slate-200/80",
          "bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70",
        )}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label={t("brochure.back")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5",
                "text-sm font-medium text-slate-600",
                "hover:bg-slate-100 hover:text-slate-900",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
                "transition-colors",
              )}
            >
              <Icon
                name="ArrowLeft"
                size={16}
                className={isRtl ? "rotate-180" : ""}
                aria-hidden="true"
              />
              <span className="hidden sm:inline">{t("brochure.back")}</span>
            </button>
            <span aria-hidden="true" className="text-slate-300">/</span>
            {moduleEntry && (
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                    moduleEntry.accent.surface,
                  )}
                >
                  <Icon
                    name={moduleEntry.iconName}
                    size={14}
                    className={moduleEntry.accent.iconText}
                    aria-hidden="true"
                  />
                </span>
                <span className="truncate text-sm font-semibold text-slate-900">
                  {moduleName}
                </span>
              </span>
            )}
          </div>

          {primaryCta.kind === "button" ? (
            <button
              type="button"
              onClick={handleStartTrial}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-3.5 py-2",
                accent.primaryBg,
                accent.primaryBgHover,
                "text-xs font-semibold text-white shadow-sm",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                accent.primaryRing,
                "transition-all sm:text-sm",
              )}
            >
              <Icon name="Rocket" size={14} aria-hidden="true" />
              <span>{primaryCta.label}</span>
            </button>
          ) : (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1",
                "bg-emerald-50 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200",
              )}
            >
              <Icon name="CheckCircle2" size={12} aria-hidden="true" />
              {primaryCta.label}
            </span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 md:px-8">
        {/* ===== Hero ===== */}
        <section
          className={cn(
            "relative isolate overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
            accent.heroGradientFrom,
            accent.heroGradientTo,
            "mt-8 px-6 py-10 md:mt-12 md:px-12 md:py-14",
          )}
        >
          {/* Decorative blurred orb in the trailing corner — subtle, RTL-safe. */}
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute -top-24 h-72 w-72 rounded-full opacity-60 blur-3xl",
              accent.chipBg,
              isRtl ? "-left-24" : "-right-24",
            )}
          />

          <div
            className={cn(
              "relative grid items-center gap-8",
              hero.illustration ? "md:grid-cols-5" : "md:grid-cols-1",
            )}
          >
            <div className={hero.illustration ? "md:col-span-3" : ""}>
              <p
                className={cn(
                  "inline-flex items-center rounded-full px-3 py-1",
                  "text-xs font-semibold uppercase tracking-[0.18em]",
                  accent.chipBg,
                  accent.chipText,
                )}
              >
                {hero.eyebrow}
              </p>
              <h1 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-tight text-slate-900 md:text-5xl">
                {hero.headline}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
                {hero.subheadline}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {!isPaid && (
                  <button
                    type="button"
                    onClick={handleStartTrial}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-lg px-5 py-2.5",
                      accent.primaryBg,
                      accent.primaryBgHover,
                      "text-sm font-semibold text-white shadow-sm",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      accent.primaryRing,
                      "transition-all",
                    )}
                  >
                    <Icon name="Rocket" size={16} aria-hidden="true" />
                    <span>{t("Start free trial")}</span>
                    <Icon
                      name="ArrowRight"
                      size={14}
                      className={isRtl ? "rotate-180" : ""}
                      aria-hidden="true"
                    />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleBackToWorkspace}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg px-4 py-2.5",
                    "text-sm font-medium text-slate-700",
                    "hover:bg-white/70 hover:text-slate-900",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
                    "transition-all",
                  )}
                >
                  {t("brochure.backToWorkspace")}
                </button>
              </div>
            </div>

            {hero.illustration && (
              <div className="relative md:col-span-2">
                <div className="mx-auto w-full max-w-sm">{hero.illustration}</div>
              </div>
            )}
          </div>
        </section>

        {/* ===== Metric strip ===== */}
        {metrics && metrics.length > 0 && (
          <section
            aria-label={t("brochure.metrics.heading")}
            className="mt-10 grid gap-4 sm:grid-cols-3"
          >
            {metrics.map((m, i) => (
              <div
                key={i}
                className={cn(
                  "rounded-2xl border border-slate-200 bg-white p-6",
                  "shadow-[0_1px_2px_rgba(15,23,42,0.03)]",
                )}
              >
                <p
                  className={cn(
                    "text-3xl font-semibold tracking-tight tabular-nums md:text-4xl",
                    accent.metricText,
                  )}
                >
                  {m.value}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.label}</p>
              </div>
            ))}
          </section>
        )}

        {/* ===== Feature grid ===== */}
        <section className="mt-16" aria-labelledby="brochure-features-heading">
          <div className="flex items-baseline justify-between">
            <h2
              id="brochure-features-heading"
              className="text-sm font-semibold uppercase tracking-wider text-slate-500"
            >
              {t("brochure.features.heading")}
            </h2>
            <span className="text-xs text-slate-400 tabular-nums">
              {t("brochure.features.count", { count: features.length })}
            </span>
          </div>

          <ul
            className={cn(
              "mt-6 grid gap-4",
              "grid-cols-1 sm:grid-cols-2",
              featureGridCols,
            )}
          >
            {features.map((f, i) => (
              <li
                key={i}
                className={cn(
                  "group flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5",
                  "shadow-[0_1px_2px_rgba(15,23,42,0.03)]",
                  "transition-all duration-200",
                  "hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]",
                )}
              >
                <span
                  className={cn(
                    "inline-flex h-10 w-10 items-center justify-center rounded-lg",
                    accent.chipBg,
                  )}
                >
                  <Icon
                    name={f.icon}
                    size={18}
                    className={accent.chipText}
                    aria-hidden="true"
                  />
                </span>
                <div className="space-y-1.5">
                  <p className="text-base font-semibold text-slate-900">{f.title}</p>
                  <p className="text-sm leading-relaxed text-slate-600">{f.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ===== Mockup ===== */}
        {mockup && (
          <section className="mt-16" aria-labelledby="brochure-mockup-heading">
            <div className="flex items-baseline justify-between">
              <h2
                id="brochure-mockup-heading"
                className="text-sm font-semibold uppercase tracking-wider text-slate-500"
              >
                {t("brochure.mockup.heading")}
              </h2>
            </div>
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:p-8">
              {/* Faux browser chrome — three dots + url pill. Cheap signal that
                  this is a screenshot, not a feature card. */}
              <div className="mb-3 flex items-center gap-2 px-1">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-300/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/70" />
                <span className="ms-2 hidden truncate rounded-md bg-white px-2 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-slate-200 sm:inline-block">
                  {t("brochure.mockup.fakeUrl", { slug })}
                </span>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-6">
                {mockup.content}
              </div>
              <p className="mt-3 text-center text-xs text-slate-400">{mockup.title}</p>
            </div>
          </section>
        )}

        {/* ===== Use cases ===== */}
        {useCases && useCases.length > 0 && (
          <section className="mt-16" aria-labelledby="brochure-usecases-heading">
            <div className="flex items-baseline justify-between">
              <h2
                id="brochure-usecases-heading"
                className="text-sm font-semibold uppercase tracking-wider text-slate-500"
              >
                {t("brochure.useCases.heading")}
              </h2>
            </div>
            <ul
              className={cn(
                "mt-6 grid gap-4",
                useCases.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3",
              )}
            >
              {useCases.map((u, i) => (
                <li
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
                >
                  <p
                    className={cn(
                      "text-xs font-semibold uppercase tracking-[0.16em]",
                      accent.chipText,
                    )}
                  >
                    {u.persona}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-800">{u.scenario}</p>
                  <div className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-slate-600">
                    <Icon
                      name="ArrowRight"
                      size={14}
                      className={cn("mt-1 shrink-0 text-slate-400", isRtl ? "rotate-180" : "")}
                      aria-hidden="true"
                    />
                    <span>{u.outcome}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ===== Testimonial ===== */}
        {testimonial && (
          <section className="mt-16">
            <blockquote className="rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.03)] md:p-10">
              <Icon
                name="Quote"
                size={20}
                className={cn("mb-4", accent.chipText)}
                aria-hidden="true"
              />
              <p className="text-lg leading-relaxed text-slate-800 md:text-xl">
                &ldquo;{testimonial.quote}&rdquo;
              </p>
              <footer className="mt-5 flex items-center gap-2 text-sm">
                <span className="font-semibold text-slate-900">{testimonial.author}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{testimonial.role}</span>
              </footer>
            </blockquote>
          </section>
        )}

        {/* ===== Closing CTA band ===== */}
        <section
          className={cn(
            "relative isolate my-16 overflow-hidden rounded-3xl border border-slate-200/80",
            "bg-gradient-to-br",
            accent.heroGradientFrom,
            accent.heroGradientTo,
            "px-6 py-12 text-center md:px-12 md:py-16",
          )}
        >
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-x-0 top-0 mx-auto h-40 w-40 rounded-full opacity-50 blur-3xl",
              accent.chipBg,
            )}
          />
          <div className="relative">
            <h2 className="text-2xl font-semibold leading-tight tracking-tight text-slate-900 md:text-3xl">
              {t("brochure.closing.headline", { module: moduleName })}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
              {t("brochure.closing.sub")}
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              {!isPaid && (
                <button
                  type="button"
                  onClick={handleStartTrial}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg px-5 py-2.5",
                    accent.primaryBg,
                    accent.primaryBgHover,
                    "text-sm font-semibold text-white shadow-sm",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    accent.primaryRing,
                    "transition-all",
                  )}
                >
                  <Icon name="Rocket" size={16} aria-hidden="true" />
                  <span>{t("Start free trial")}</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleSeePricing}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5",
                  "text-sm font-medium text-slate-700",
                  "hover:bg-slate-50 hover:text-slate-900",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
                  "transition-all",
                )}
              >
                {t("See pricing")}
              </button>
              <button
                type="button"
                onClick={handleContactUs}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5",
                  "text-sm font-medium text-slate-700",
                  "hover:bg-slate-50 hover:text-slate-900",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
                  "transition-all",
                )}
              >
                {t("Talk to us")}
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModuleBrochure;
